import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useDialogInfoContext } from "../components/Dialogs/context";

import { resetDialog } from "../ducks/dialogs/dialogs";
import { useDialog } from "./useDialog";
import { DIALOGS } from "../components/Dialogs/constants";

export const useCopyFeatureDialog = () => useDialog(DIALOGS.COPY_FEATURE);
export const useDeleteFeatureDialog = () => useDialog(DIALOGS.DELETE_FEATURE);
export const useDeleteResourcesDialog = () => useDialog(DIALOGS.DELETE_RESOURCES);
export const useEditResourceDialog = () => useDialog(DIALOGS.EDIT_RESOURCE);
export const useCopyResourceDialog = () => useDialog(DIALOGS.COPY_RESOURCE);
export const useCopyLayerFromProjectDialog = () => useDialog(DIALOGS.COPY_LAYER_FROM_PROJECT);
export const useBulkCopyResourceDialog = () => useDialog(DIALOGS.BULK_COPY_RESOURCE);
export const useImportDialog = () => useDialog(DIALOGS.IMPORT);
export const useExportDialog = () => useDialog(DIALOGS.EXPORT);
export const useNewLayerDialog = () => useDialog(DIALOGS.NEW_LAYER);
export const useUploadDataDialog = () => useDialog(DIALOGS.UPLOAD_DATA);
export const useNewLayerFromData = () => useDialog(DIALOGS.NEW_LAYER_FROM_TABLE);
export const useAddTmsDialog = () => useDialog(DIALOGS.ADD_TMS_SERVICE);
export const useAddBookmarkDialog = () => useDialog(DIALOGS.ADD_BOOKMARK);
export const useInfoResourceDialog = () => useDialog(DIALOGS.INFO_RESOURCE);
export const useSizeRangesDialog = () => useDialog(DIALOGS.SIZE_RANGES);
export const useClassesRangeCalculationDialog = () => useDialog(DIALOGS.CLASSES_RANGE_CALCULATION);
export const useClassificationTitleDialog = () => useDialog(DIALOGS.CLASSIFICATION_TITLE);
export const useConfirmDialog = () => useDialog(DIALOGS.CONFIRM);
export const useConfigFilterDialog = () => useDialog(DIALOGS.CONFIG_FILTER);
export const useConfigLayerDialog = () => useDialog(DIALOGS.CONFIG_LAYER);
export const useScaleRestrictionDialog = () => useDialog(DIALOGS.SCALE_RESTRICTION);
export const useLimitsExceededDialog = () => useDialog(DIALOGS.LIMITS_EXCEEDED);
export const useLayerFolderDialog = () => useDialog(DIALOGS.LAYER_FOLDER);
export const useAddRelatedLayerDialog = () => useDialog(DIALOGS.ADD_RELATED_LAYER);
export const useCloseFeatureCardDialog = () => useDialog(DIALOGS.CLOSE_FEATURE_CARD);
export const useFeaturesMultiEditDialog = () => useDialog(DIALOGS.FEATURES_MULTI_EDIT);
export const useAddImageAttribute = () => useDialog(DIALOGS.ADD_IMAGE_ATTRIBUTE);
export const useCalculatedAttributesVertex = () => useDialog(DIALOGS.CALCULATED_ATTRIBUTES_VERTEX);
export const useProjectAccessDialog = () => useDialog(DIALOGS.PROJECT_ACCESS);
export const useLayerAccessDialog = () => useDialog(DIALOGS.LAYER_ACCESS);
export const useTableAccessDialog = () => useDialog(DIALOGS.TABLE_ACCESS);
export const useWmsLinkDialog = () => useDialog(DIALOGS.WMS_LINK);
export const useDeleteLayerDialog = () => useDialog(DIALOGS.DELETE_LAYER);
export const useDeleteGroupDialog = () => useDialog(DIALOGS.DELETE_GROUP);
export const useScaleSegmentationDialog = () => useDialog(DIALOGS.SCALE_SEGMENTATION);
export const useEditCoordinatesDialog = () => useDialog(DIALOGS.EDIT_COORDINATES);
export const useBasemapSettingsDialog = () => useDialog(DIALOGS.BASEMAP_SETTINGS);
export const useBasemapAddDialog = () => useDialog(DIALOGS.BASEMAP_ADD);
export const useClusterSizeRangesDialog = () => useDialog(DIALOGS.CLUSTER_SIZE_RANGES);
export const useProjectionChoiceDialog = () => useDialog(DIALOGS.PROJECTION_CHOICE);
export const useSelectResourcesDialog = () => useDialog(DIALOGS.SELECT_RESOURCES);
export const useSelectFilesDialog = () => useDialog(DIALOGS.SELECT_FILES);
export const useAddByLinkDialog = () => useDialog(DIALOGS.ADD_BY_LINK);
export const useSaveQLSLayerDialog = () => useDialog(DIALOGS.SAVE_QLS_LAYER);
export const useProjectInfoDialog = () => useDialog(DIALOGS.PROJECT_INFO);
export const useSaveTempSourceLayer = () => useDialog(DIALOGS.SAVE_TEMP_SOURCE_LAYER);
export const useSelectResourcePath = () => useDialog(DIALOGS.SELECT_RESOURCE_PATH);

export const useDialogReset = () => {
  const dispatch = useDispatch();

  return useCallback(
    <E extends unknown>(e?: E) => {
      if (e instanceof Event) {
        e.preventDefault();
        e.stopPropagation();
      }
      dispatch(resetDialog());
    },
    [dispatch],
  );
};

type DialogInfo<R, O> = {
  isOpen?: boolean;
  name: string;
  resources?: R;
  options?: O;
};

type DialogInfoActions = {
  closeDialog: VoidFunction;
};

export const useDialogInfo = <R extends string[], O = Record<string, unknown>>(
  resourceDependent?: boolean,
): DialogInfo<R, O> & DialogInfoActions => {
  const { resources = [], options = {}, type } = useDialogInfoContext();
  const closeDialog = useDialogReset();

  useEffect(() => {
    if (resourceDependent && !resources.length) {
      closeDialog();
    }
  }, [resources, options, resourceDependent, closeDialog]);

  return {
    isOpen: !!type,
    name: type,
    resources: resources as R,
    options: options as O,
    closeDialog,
  };
};
